import { useEffect, useRef, useState } from "react";
import { ChevronLeft, Ellipsis, Images, Loader2, RefreshCw, RotateCcw, Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

// In-app camera: a rounded slide-up frame with the live preview, a back button, a big
// white shutter, a torch/flash toggle and a "more" menu (flip camera / choose from library). Returns a JPEG File.
// Falls back to the OS camera picker when the browser can't stream video.
export default function CameraSheet({ open, onOpenChange, onCapture, facing = "environment", title = "Camera", mode: initialMode = "photo", allowVideo = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const fallbackRef = useRef(null);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const [face, setFace] = useState(facing);
  const [ready, setReady] = useState(false);
  const [shot, setShot] = useState(null); // { url, file, kind }
  const [menu, setMenu] = useState(false);
  const [error, setError] = useState("");
  const [torchable, setTorchable] = useState(false);
  const [flash, setFlash] = useState(false); // torch on (back) or screen-flash armed (front)
  const [screenFlash, setScreenFlash] = useState(false);
  const [mode, setMode] = useState(initialMode); // "photo" | "video"
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const MAX_SECS = 60;

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  useEffect(() => {
    if (!open) { stop(); setShot(null); setMenu(false); setError(""); setReady(false); setFlash(false); setRecording(false); setSecs(0); clearInterval(timerRef.current); return undefined; }
    setFace(facing); setMode(initialMode);
    return stop;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || shot) return undefined;
    let cancelled = false;
    setReady(false);
    stop();
    if (!navigator.mediaDevices?.getUserMedia) { setError("unsupported"); return undefined; }
    const wantAudio = mode === "video";
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: face }, width: { ideal: 1920 }, height: { ideal: 1920 } }, audio: wantAudio })
      .catch((e) => (wantAudio ? navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: face } }, audio: false }) : Promise.reject(e)))
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
        const track = s.getVideoTracks()[0];
        const caps = track?.getCapabilities ? track.getCapabilities() : {};
        setTorchable(!!caps.torch);
        if (flash && caps.torch) track.applyConstraints({ advanced: [{ torch: true }] }).catch(() => {});
        setReady(true);
      })
      .catch(() => { if (!cancelled) setError("denied"); });
    return () => { cancelled = true; };
  }, [open, face, shot, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRec = () => {
    const s = streamRef.current;
    if (!s || typeof MediaRecorder === "undefined") { toast.info("Video recording isn't available here — use your phone's camera"); fallbackRef.current?.click(); return; }
    const type = ["video/mp4", "video/webm;codecs=vp9", "video/webm"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
    const chunks = [];
    const rec = new MediaRecorder(s, type ? { mimeType: type } : undefined);
    rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    rec.onstop = () => {
      clearInterval(timerRef.current);
      setRecording(false);
      const mime = rec.mimeType || type || "video/webm";
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const b = new Blob(chunks, { type: mime });
      if (!b.size) return;
      const file = new File([b], `video-${Date.now()}.${ext}`, { type: mime });
      setShot({ url: URL.createObjectURL(b), file, kind: "video" });
      stop();
    };
    recRef.current = rec;
    rec.start(250);
    setRecording(true); setSecs(0);
    const t0 = Date.now();
    timerRef.current = setInterval(() => {
      const el = Math.floor((Date.now() - t0) / 1000);
      setSecs(el);
      if (el >= MAX_SECS) stopRec();
    }, 250);
  };
  const stopRec = () => { try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch { /* already stopped */ } };

  const toggleFlash = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (torchable && track) {
      try { await track.applyConstraints({ advanced: [{ torch: !flash }] }); setFlash((f) => !f); }
      catch { toast.error("Couldn't switch the torch"); }
      return;
    }
    if (face === "user") { setFlash((f) => !f); return; } // screen lights up white on capture
    toast.info("Flash isn't available on this device");
  };

  useEffect(() => {
    // No live preview possible: hand over to the OS camera straight away.
    if (open && error) fallbackRef.current?.click();
  }, [open, error]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const take = () => {
      const c = document.createElement("canvas");
      c.width = v.videoWidth; c.height = v.videoHeight;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0); // saved un-mirrored (like the phone's own camera); only the live preview is mirrored
      c.toBlob((b) => {
        setScreenFlash(false);
        if (!b) return;
        const file = new File([b], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        setShot({ url: URL.createObjectURL(b), file, kind: "photo" });
        stop();
      }, "image/jpeg", 0.9);
    };
    if (flash && !torchable && face === "user") { setScreenFlash(true); setTimeout(take, 350); } // light the face with the screen
    else take();
  };
  const onShutter = () => { if (mode === "video") (recording ? stopRec() : startRec()); else snap(); };

  const use = () => { if (shot) { onCapture?.(shot.file); onOpenChange(false); } };
  const fromFile = (f) => { if (f) { onCapture?.(f); } onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent solid hideCloseButton className="max-w-md p-0 gap-0 overflow-hidden bg-black border-0 rounded-[28px] max-sm:rounded-b-none sm:rounded-[28px]" data-testid="camera-sheet">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">Take a photo</DialogDescription>
        <div className="relative w-full h-[78dvh] sm:h-[640px] bg-black">
          {shot ? (
            shot.kind === "video"
              ? <video src={shot.url} controls playsInline autoPlay className="absolute inset-0 h-full w-full object-contain bg-black" data-testid="camera-preview-video" />
              : <img src={shot.url} alt="Preview" className="absolute inset-0 h-full w-full object-cover" data-testid="camera-preview-img" />
          ) : (
            <video ref={videoRef} playsInline muted autoPlay className={`absolute inset-0 h-full w-full object-cover ${face === "user" ? "-scale-x-100" : ""}`} data-testid="camera-video" />
          )}
          {recording && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 px-3 h-8 rounded-full bg-black/60 backdrop-blur text-white text-sm font-semibold tabular-nums flex items-center gap-2" data-testid="camera-rec-timer">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF3B30] animate-pulse" /> {String(Math.floor(secs / 60)).padStart(1, "0")}:{String(secs % 60).padStart(2, "0")}
            </div>
          )}
          {!ready && !shot && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80"><Loader2 className="animate-spin" size={28} /></div>
          )}
          {screenFlash && <div className="absolute inset-0 bg-white z-20" data-testid="camera-screen-flash" />}
          {!shot && !error && (
            <button type="button" onClick={toggleFlash} aria-label={flash ? "Flash on" : "Flash off"} aria-pressed={flash}
              className={`absolute top-4 right-4 z-10 h-11 w-11 rounded-full backdrop-blur flex items-center justify-center transition-colors ${flash ? "bg-[#FFD60A] text-black" : "bg-black/55 text-white"}`}
              data-testid="camera-flash">
              {flash ? <Zap size={20} fill="currentColor" /> : <ZapOff size={20} />}
            </button>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center text-white/90">
              <p className="text-[15px]">{error === "denied" ? "Camera access is off. Allow it in your phone settings, or pick a photo instead." : "Live camera isn't available here — use your phone's camera."}</p>
              <button type="button" onClick={() => fallbackRef.current?.click()} className="h-10 px-4 rounded-full bg-white text-black text-sm font-semibold" data-testid="camera-fallback-btn">Open phone camera</button>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-16 bg-gradient-to-t from-black/60 to-transparent">
            {shot ? (
              <div className="flex items-center justify-between px-8">
                <button type="button" onClick={() => setShot(null)} className="h-12 px-5 rounded-full bg-black/55 backdrop-blur text-white text-sm font-medium inline-flex items-center gap-2" data-testid="camera-retake"><RotateCcw size={16} /> Retake</button>
                <button type="button" onClick={use} className="h-12 px-6 rounded-full bg-white text-black text-sm font-semibold" data-testid="camera-use-photo">{shot.kind === "video" ? "Use video" : "Use photo"}</button>
              </div>
            ) : (
              <div className="relative flex flex-col items-center gap-4">
                {allowVideo && !recording && (
                  <div className="flex items-center gap-1 rounded-full bg-black/55 backdrop-blur p-1 text-[13px] font-semibold" data-testid="camera-mode-switch">
                    {["photo", "video"].map((m) => (
                      <button key={m} type="button" onClick={() => setMode(m)} className={`h-8 px-4 rounded-full transition-colors ${mode === m ? "bg-white text-black" : "text-white/85"}`} data-testid={`camera-mode-${m}`}>{m === "photo" ? "Photo" : "Video"}</button>
                    ))}
                  </div>
                )}
                <div className="relative w-full flex items-center justify-between px-7">
                  <button type="button" onClick={() => onOpenChange(false)} aria-label="Back" className="h-12 w-12 rounded-full bg-black/55 backdrop-blur text-white flex items-center justify-center" data-testid="camera-back"><ChevronLeft size={24} /></button>
                  <button type="button" onClick={onShutter} disabled={!ready} aria-label={mode === "video" ? (recording ? "Stop recording" : "Record video") : "Take photo"}
                    className="h-[72px] w-[72px] rounded-full border-[5px] border-white bg-white/20 flex items-center justify-center transition-transform active:scale-95 disabled:opacity-40" data-testid="camera-shutter">
                    {mode === "video"
                      ? <span className={`bg-[#FF3B30] transition-all duration-200 ${recording ? "h-7 w-7 rounded-md" : "h-14 w-14 rounded-full"}`} />
                      : <span className="h-14 w-14 rounded-full bg-white" />}
                  </button>
                  <button type="button" onClick={() => setMenu((m) => !m)} aria-label="More" className="h-12 w-12 rounded-full bg-black/55 backdrop-blur text-white flex items-center justify-center" data-testid="camera-more"><Ellipsis size={22} /></button>
                  {menu && (
                    <div className="absolute right-7 bottom-14 w-52 rounded-2xl bg-black/80 backdrop-blur text-white text-sm overflow-hidden shadow-xl" data-testid="camera-more-menu">
                      <button type="button" onClick={() => { setFace((f) => (f === "user" ? "environment" : "user")); setMenu(false); }} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10" data-testid="camera-flip"><RefreshCw size={16} /> Flip camera</button>
                      <button type="button" onClick={() => { setMenu(false); fileRef.current?.click(); }} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10 border-t border-white/10" data-testid="camera-library"><Images size={16} /> Choose from library</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <input ref={fileRef} type="file" accept={mode === "video" ? "video/*" : "image/*"} className="hidden" onChange={(e) => { fromFile(e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={fallbackRef} type="file" accept={mode === "video" ? "video/*" : "image/*"} capture={facing} className="hidden" onChange={(e) => { fromFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="camera-fallback-input" />
      </DialogContent>
    </Dialog>
  );
}
